import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type {
  PromotionBundle,
  PromotionBundleSavePayload,
  PromotionBundleStatus,
} from '@/api/promotion-bundles'
import {
  PromotionBundleProductPickerDialog,
  type PromotionBundlePickedItem,
} from './PromotionBundleProductPickerDialog'

interface EditablePromotionBundleItem extends PromotionBundlePickedItem {
  id?: number
  row_key: string
}

interface PromotionBundleDialogProps {
  open: boolean
  bundle: PromotionBundle | null
  isSaving: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: PromotionBundleSavePayload) => void
}

function money(value: number | undefined) {
  return `¥${(value ?? 0).toFixed(2)}`
}

function toInt(value: string | number, min: number) {
  const parsed = typeof value === 'number' ? value : Number.parseInt(value, 10)
  if (!Number.isFinite(parsed)) return min
  return Math.max(min, Math.trunc(parsed))
}

function parsePrice(value: string) {
  const trimmed = value.trim()
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  return parsed
}

export function PromotionBundleDialog({
  open,
  bundle,
  isSaving,
  onOpenChange,
  onSubmit,
}: PromotionBundleDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [promotionPrice, setPromotionPrice] = useState('')
  const [status, setStatus] = useState<PromotionBundleStatus>('enabled')
  const [sort, setSort] = useState('0')
  const [items, setItems] = useState<EditablePromotionBundleItem[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (!open) return

    setName(bundle?.name ?? '')
    setDescription(bundle?.description ?? '')
    setPromotionPrice(bundle ? String(bundle.promotion_price) : '')
    setStatus(bundle?.status ?? 'enabled')
    setSort(String(bundle?.sort ?? 0))
    setItems((bundle?.items ?? []).map((item, index) => ({
      row_key: item.id ? `saved-${item.id}` : `saved-${item.sku_unique}-${index}`,
      id: item.id,
      product_id: item.crmeb_product_id,
      sku_unique: item.sku_unique,
      product_name_snapshot: item.product_name_snapshot,
      sku_name_snapshot: item.sku_name_snapshot,
      product_image_snapshot: item.product_image_snapshot,
      unit_price_snapshot: item.unit_price_snapshot,
      quantity: item.quantity,
      line_original_price: item.line_original_price,
      sort: item.sort || index + 1,
    })))
  }, [bundle, open])

  const originalPrice = useMemo(
    () => items.reduce((sum, item) => sum + item.unit_price_snapshot * item.quantity, 0),
    [items],
  )

  const updateItem = (index: number, patch: Partial<EditablePromotionBundleItem>) => {
    setItems((prev) => prev.map((item, itemIndex) => {
      if (itemIndex !== index) return item
      const next = { ...item, ...patch }
      return {
        ...next,
        line_original_price: next.unit_price_snapshot * next.quantity,
      }
    }))
  }

  const handleAddItem = (item: PromotionBundlePickedItem) => {
    setItems((prev) => [
      ...prev,
      {
        ...item,
        row_key: `new-${item.sku_unique}-${Date.now()}-${prev.length}`,
        sort: prev.length + 1,
      },
    ])
  }

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('请输入组合名称')
      return
    }
    if (items.length === 0) {
      toast.error('请至少添加一个商品')
      return
    }

    const price = parsePrice(promotionPrice)
    if (price === null) {
      toast.error('促销价最多保留 2 位小数')
      return
    }
    if (price <= 0 || price > originalPrice) {
      toast.error('促销价必须大于 0 且不能超过原价')
      return
    }

    const normalizedItems = items.map((item) => ({
      ...item,
      quantity: toInt(item.quantity, 1),
      sort: toInt(item.sort, 0),
    }))

    const invalidItem = normalizedItems.find((item) => !item.sku_unique || item.quantity <= 0)
    if (invalidItem) {
      toast.error('商品数量必须大于 0')
      return
    }

    onSubmit({
      name: name.trim(),
      description: description.trim(),
      promotion_price: price,
      status,
      sort: toInt(sort, 0),
      items: normalizedItems.map((item, index) => ({
        product_id: item.product_id,
        sku_unique: item.sku_unique,
        quantity: item.quantity,
        sort: index + 1,
      })),
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-[calc(100vw-2rem)] overflow-hidden p-0 lg:max-w-5xl">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>{bundle ? '编辑促销组合' : '新建促销组合'}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[calc(90vh-132px)] space-y-5 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_160px_160px]">
              <div className="space-y-2">
                <Label htmlFor="bundle-name">组合名称</Label>
                <Input
                  id="bundle-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="例如：护肤体验组合"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bundle-status">状态</Label>
                <Select value={status} onValueChange={(value) => setStatus(value as PromotionBundleStatus)}>
                  <SelectTrigger id="bundle-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enabled">启用</SelectItem>
                    <SelectItem value="disabled">停用</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bundle-sort">排序</Label>
                <Input
                  id="bundle-sort"
                  type="number"
                  min={0}
                  value={sort}
                  onChange={(event) => setSort(String(toInt(event.target.value, 0)))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bundle-description">说明</Label>
              <Textarea
                id="bundle-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="内部备注，可留空"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 rounded-lg border bg-muted/20 p-4 md:grid-cols-3">
              <div>
                <p className="text-xs text-muted-foreground">原价合计</p>
                <p className="mt-1 text-lg font-semibold">{money(originalPrice)}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bundle-promotion-price">促销价</Label>
                <Input
                  id="bundle-promotion-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={promotionPrice}
                  onChange={(event) => setPromotionPrice(event.target.value)}
                  onBlur={() => {
                    const price = parsePrice(promotionPrice)
                    if (price !== null) setPromotionPrice(price.toFixed(2))
                  }}
                  placeholder="0.00"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">优惠金额</p>
                <p className="mt-1 text-lg font-semibold text-red-600">
                  {money(Math.max(0, originalPrice - (Number(promotionPrice) || 0)))}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">组合商品</h3>
                  <p className="text-xs text-muted-foreground">保存时仅提交商品 SKU、数量和排序，快照由后端重新校验</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  添加商品
                </Button>
              </div>

              {items.length === 0 ? (
                <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
                  还没有商品，点击添加商品选择 CRMEB SKU
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <div className="min-w-[780px]">
                    <div className="grid grid-cols-[44px_minmax(0,1fr)_112px_96px_96px_80px_48px] items-center gap-3 border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                      <span />
                      <span>商品</span>
                      <span className="text-right">单价</span>
                      <span className="text-right">数量</span>
                      <span className="text-right">小计</span>
                      <span className="text-right">顺序</span>
                      <span />
                    </div>
                    <div className="divide-y">
                      {items.map((item, index) => (
                        <div
                          key={item.row_key}
                          className="grid grid-cols-[44px_minmax(0,1fr)_112px_96px_96px_80px_48px] items-center gap-3 px-3 py-2"
                        >
                          <img
                            src={item.product_image_snapshot}
                            alt={item.product_name_snapshot}
                            className="h-10 w-10 rounded object-cover"
                          />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{item.product_name_snapshot}</p>
                            <p className="truncate text-xs text-muted-foreground">{item.sku_name_snapshot || '默认规格'}</p>
                          </div>
                          <div className="text-right text-sm">{money(item.unit_price_snapshot)}</div>
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            value={String(item.quantity)}
                            onChange={(event) => updateItem(index, { quantity: toInt(event.target.value, 1) })}
                            className="h-8 text-right"
                          />
                          <div className="text-right text-sm font-medium">{money(item.line_original_price)}</div>
                          <div className="text-right text-sm text-muted-foreground">{index + 1}</div>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">删除商品</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              取消
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PromotionBundleProductPickerDialog
        open={pickerOpen}
        selectedCount={items.length}
        onOpenChange={setPickerOpen}
        onSelect={handleAddItem}
      />
    </>
  )
}
